/*
 * lx1-spec-tabs.js — behaviour for the LX1 product-details tab band (markup: snippets/lx1-spec-tabs.liquid, styles:
 * assets/lx1-spec-tabs.css). Used by sections/lx1-pdp-tabs.liquid and the older sections/moasure-lx1.liquid.
 *
 * Wires up every `[data-lx1s]` band on the page: the tab row (click plus arrow/Home/End keys), the phone accordion
 * (one item open at a time),
 * and the "What's in the box" scroll-snap slider (dots, prev/next arrows, swipe, arrow keys). The first state (which
 * tab is selected, which accordion item is open, the disabled "previous" arrow) is already in the markup, so nothing
 * changes on screen when this runs.
 *
 * Loaded with `defer`. Safe to load more than once: each band is bound once (`root.__lx1s`), and the theme-editor
 * listener is added once (`window.lx1SpecTabs`). No dependencies; deliberately no data-instant-* hooks, so neither
 * instant-core nor moasure-lx1-runtime.js tries to drive these tabs.
 */
(function () {
  function initKit(kit) {
    var track = kit.querySelector('.lx1s__track');
    var cards = Array.prototype.slice.call(track.children);
    var dots = Array.prototype.slice.call(kit.querySelectorAll('.lx1s__dot'));
    var prev = kit.querySelector('.lx1s__arrow-btn[data-dir="-1"]'), next = kit.querySelector('.lx1s__arrow-btn[data-dir="1"]');
    var target = -1, settle = 0;
    // A card's scroll position: its offset inside the track, less the track's own edge padding (the content-block gutter, --mo-block-gutter).
    function pos(c) { return c.offsetLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0); }
    function nearest() {
      var max = track.scrollWidth - track.clientWidth;
      if (max > 0 && track.scrollLeft >= max - 2) return cards.length - 1;
      var best = 0, bestD = Infinity;
      cards.forEach(function (c, i) { var d = Math.abs(pos(c) - track.scrollLeft); if (d < bestD) { bestD = d; best = i; } });
      return best;
    }
    // While a smooth scroll is running, the card it is heading to counts as current, so quick repeat clicks keep advancing.
    function current() { return target > -1 ? target : nearest(); }
    function update() {
      var i = current(), max = track.scrollWidth - track.clientWidth;
      dots.forEach(function (d, j) { if (j === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      if (prev) prev.disabled = i === 0 && track.scrollLeft <= 2;
      if (next) next.disabled = i === cards.length - 1 || (target < 0 && track.scrollLeft >= max - 2);
    }
    function go(i) {
      target = Math.max(0, Math.min(cards.length - 1, i));
      var max = track.scrollWidth - track.clientWidth;
      var left = Math.max(0, Math.min(pos(cards[target]), max));
      if (Math.abs(left - track.scrollLeft) < 2) target = -1;
      else track.scrollTo({ left: left, behavior: 'smooth' });
      update();
    }
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });
    [prev, next].forEach(function (b) { if (b) b.addEventListener('click', function () { go(current() + Number(b.getAttribute('data-dir'))); }); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(current() + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(current() - 1); }
    });
    var raf = 0;
    track.addEventListener('scroll', function () {
      cancelAnimationFrame(raf); raf = requestAnimationFrame(update);
      clearTimeout(settle); settle = setTimeout(function () { target = -1; update(); }, 150);
    }, { passive: true });
    track.addEventListener('pointerdown', function () { target = -1; }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function init(root) {
    if (root.__lx1s) return;
    root.__lx1s = true;

    // Tabs (desktop and tablet)
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.lx1s__tab'));
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var p = document.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { select(t, false); });
      t.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') n = tabs[0];
        else if (e.key === 'End') n = tabs[tabs.length - 1];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });

    // Kit sliders (one in the tab pane, one in the phone accordion)
    Array.prototype.forEach.call(root.querySelectorAll('[data-lx1s-kit]'), initKit);

    // Re-sync a slider's dots and arrows when its tab is shown again.
    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        var p = document.getElementById(t.getAttribute('aria-controls'));
        var tr = p && p.querySelector('.lx1s__track');
        if (tr) tr.dispatchEvent(new Event('scroll'));
      });
    });

    // Accordion (phones): one item open at a time, as on the M2 page — opening an item closes the others.
    var heads = Array.prototype.slice.call(root.querySelectorAll('.lx1s__head'));
    function setOpen(h, open) {
      h.parentNode.classList.toggle('is-open', open);
      h.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    heads.forEach(function (h) {
      h.addEventListener('click', function () {
        var open = !h.parentNode.classList.contains('is-open');
        if (open) heads.forEach(function (o) { if (o !== h) setOpen(o, false); });
        setOpen(h, open);
      });
    });
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-lx1s]'), init);
  }

  if (!window.lx1SpecTabs) {
    window.lx1SpecTabs = { init: initAll };
    // Theme editor: a section re-rendered after a settings change arrives as new markup that needs binding.
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  }
  initAll(document);
})();
